import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/ledger_print";

/* ---------------- helpers ---------------- */
const toStr = (v, fb = "") => String(v ?? fb).trim();

const pad2 = (n) => String(n).padStart(2, "0");
const fmtDate = (v) => {
  if (!v) return "";
  if (typeof v === "string" && v.includes("/")) return v; // dd/MM/yyyy already
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const fmtNum2 = (v) => {
  if (v === null || v === undefined || v === "") return "0.00";
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const modeTitle = (mode) => {
  if (String(mode) === "1") return "SUPPLIER LEDGER REPORT";
  if (String(mode) === "2") return "DEPARTMENT LEDGER REPORT";
  if (String(mode) === "3") return "VEHICLE LEDGER REPORT";
  return "LEDGER REPORT";
};

/**
 * Normalize common ledger columns.
 * Your SP can return any column names; we map common possibilities.
 */
function normalizeLedgerRow(x, idx) {
  return {
    sno: idx + 1,
    date: fmtDate(x?.Date || x?.fld_date || x?.VoucherDate || x?.VDate),
    particulars: x?.Particulars || x?.fld_particulars || x?.Narration || x?.Description || "",
    vchType: x?.VchType || x?.VoucherType || x?.fld_vouchertype || "",
    vchNo: x?.VchNo || x?.VoucherNo || x?.fld_voucherno || "",
    debit: fmtNum2(x?.Debit || x?.DrAmount || x?.fld_debit || x?.dr || 0),
    credit: fmtNum2(x?.Credit || x?.CrAmount || x?.fld_credit || x?.cr || 0),
    balance: fmtNum2(x?.Balance || x?.fld_balance || x?.BalAmount || 0),
  };
}

export default function LedgerReportPrint() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ✅ Read filters from URL query string
  // Example:
  // /inventory/LedgerReport_Print?mode=1&partyId=12&partyName=ABC&fromDate=2025-01-01&toDate=2025-01-31&branchId=8
  const params = useMemo(() => {
    const obj = Object.fromEntries([...searchParams.entries()]);
    return {
      mode: obj.mode || obj.type || "1", // 1 Supplier, 2 Dept, 3 Vehicle
      branchId: obj.branchId || "Select",
      fromDate: obj.fromDate || "",
      toDate: obj.toDate || "",

      // Ledger “party” can be supplier/department/vehicle based on mode
      partyId: obj.partyId || obj.supplierId || obj.departmentId || obj.vehicleId || "Select",
      partyName: obj.partyName || obj.supplierName || obj.departmentName || obj.vehicleName || "",
      vehicleCode: obj.vehicleCode || "",
      regNo: obj.regNo || obj.vehicleRegNo || "",
    };
  }, [searchParams]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setLoading(true);
        setErr("");

        // ✅ Backend should return: { success:true, rows:[...], meta:{...} }
        const res = await axios.get(`${API_BASE}/print`, { params });
        const d = res?.data || {};
        const list = Array.isArray(d.rows) ? d.rows : [];

        if (!alive) return;
        setRows(list);
      } catch (e) {
        if (!alive) return;
        setErr(e?.response?.data?.message || e.message || "Failed to load ledger print data");
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [params]);

  const data = useMemo(() => rows.map(normalizeLedgerRow), [rows]);

  const totals = useMemo(() => {
    const t = data.reduce(
      (acc, r) => {
        const dr = Number(String(r.debit).replace(/,/g, "")) || 0;
        const cr = Number(String(r.credit).replace(/,/g, "")) || 0;
        acc.dr += dr;
        acc.cr += cr;
        return acc;
      },
      { dr: 0, cr: 0 }
    );
    return { dr: fmtNum2(t.dr), cr: fmtNum2(t.cr) };
  }, [data]);

  return (
    <div className="w-full bg-white print:max-w-none print:mx-0">
      <style>{`
        @media print {
          body { margin: 0; padding: 8px; font-size: 9pt; }
          .no-print { display: none !important; }
          table { border-collapse: collapse; font-size: 8pt; }
          th, td { border: 1px solid #000 !important; padding: 3px !important; }
        }
        @page {
          size: A4 portrait;
          margin: 0.35in;
        }
      `}</style>

      <div className="p-2 print:p-1">
        {/* Top Actions */}
        <div className="no-print flex items-center justify-between mb-3">
          <button onClick={() => navigate(-1)} className="px-4 py-2 border rounded hover:bg-gray-50">
            ← Back
          </button>
          <button
            onClick={() => window.print()}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow"
          >
            🖨️ Print
          </button>
        </div>

        {/* Loading/Error */}
        {loading ? (
          <div className="no-print p-4 border rounded bg-gray-50 text-sm">Loading ledger print data...</div>
        ) : err ? (
          <div className="no-print p-4 border rounded bg-red-50 text-sm text-red-700">{err}</div>
        ) : null}

        {/* Header */}
        <div className="border-2 border-black mb-2">
          <div className="flex items-center p-2">
            <div className="w-20 h-16 border border-black flex items-center justify-center mr-4 bg-gray-800 text-white font-bold">
              <div className="text-center">
                <div className="text-xs">NISSI</div>
                <div className="text-xs">INFOTECH</div>
              </div>
            </div>

            <div className="flex-1 text-center leading-tight">
              <h1 className="text-lg font-bold">Nissi Infotech</h1>
              <div className="text-sm font-semibold">Nissi CIVIL CONSTRUCTION</div>
              <div className="text-[10px] mt-1">
                3/42-12, Nagappa Gounder St, kavundampalayam, Coimbatore - Tamil Nadu.
                <br />
                Phone No : 9003744644 &nbsp;&nbsp; infracosoft-implementa
              </div>
              <div className="text-sm font-bold underline mt-2">{modeTitle(params.mode)}</div>
              <div className="text-[11px] mt-1">
                Party: <b>{toStr(params.partyName, "-") || "-"}</b>
                {params.mode === "3" ? (
                  <>
                    {" "}
                    | Code: <b>{toStr(params.vehicleCode, "-") || "-"}</b> | RegNo:{" "}
                    <b>{toStr(params.regNo, "-") || "-"}</b>
                  </>
                ) : null}
              </div>
            </div>

            <div className="w-44 text-right text-[10px]">
              <div>INFRA SCREEN : LEDGER</div>
              <div>
                From: <b>{toStr(params.fromDate) || "-"}</b>
              </div>
              <div>
                To: <b>{toStr(params.toDate) || "-"}</b>
              </div>
              <div>
                Branch: <b>{toStr(params.branchId) || "-"}</b>
              </div>
            </div>
          </div>
        </div>

        {/* TABLE */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border-2 border-black text-xs">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 35 }}>
                  S.No
                </th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 70 }}>
                  Date
                </th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 220 }}>
                  Particulars
                </th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 80 }}>
                  Vch Type
                </th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 80 }}>
                  Vch No
                </th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 90 }}>
                  Debit
                </th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 90 }}>
                  Credit
                </th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 100 }}>
                  Balance
                </th>
              </tr>
            </thead>

            <tbody>
              {!data?.length ? (
                <tr>
                  <td colSpan={8} className="border border-black px-2 py-2 text-center">
                    No records found.
                  </td>
                </tr>
              ) : (
                data.map((r) => (
                  <tr key={`${r.sno}-${r.vchNo}`}>
                    <td className="border border-black px-1 py-1 text-center">{r.sno}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.date}</td>
                    <td className="border border-black px-1 py-1">{r.particulars}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.vchType}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.vchNo}</td>
                    <td className="border border-black px-1 py-1 text-right">{r.debit}</td>
                    <td className="border border-black px-1 py-1 text-right">{r.credit}</td>
                    <td className="border border-black px-1 py-1 text-right">{r.balance}</td>
                  </tr>
                ))
              )}
            </tbody>

            {data?.length ? (
              <tfoot>
                <tr>
                  <td colSpan={5} className="border border-black px-2 py-1 text-right font-bold">
                    Total
                  </td>
                  <td className="border border-black px-2 py-1 text-right font-bold">{totals.dr}</td>
                  <td className="border border-black px-2 py-1 text-right font-bold">{totals.cr}</td>
                  <td className="border border-black px-2 py-1" />
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>

        <div className="mt-3 border-2 border-black p-1 text-xs text-center">
          Report Created by Infracosoft version 2.1. www.infracosoft.com
        </div>

        <div className="mt-4 text-center no-print">
          <button
            onClick={() => window.print()}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow-lg"
          >
            🖨️ Print Report
          </button>
        </div>
      </div>
    </div>
  );
}